import {graphql} from 'react-relay';
import type {Environment, MutationParameters, PayloadError, UploadableMap} from 'relay-runtime';
import Util, {getValidationErrorMessageId} from '../../../util/util';
import {trackAndCommitMutation} from '../../../../../tracking/amplitude/TrackMutation';

export type SendQuoteInput = {
	quoteId: string;
	toContactIds: string[];
	subject: string;
	bodyHtml: string;
};

const mutation = graphql`
	mutation sendQuoteMutation($input: SendQuoteInput!) {
		sendQuote(input: $input) {
			quote {
				id
				status
			}
			project {
				id
				quoteActivities(first: 100) {
					edges {
						node {
							id
							eventType
							createdAt
							clientContactName
							clientContact {
								id
							}
							reason
							person {
								id
								fullName
							}
						}
					}
				}
			}
		}
	}
`;

// Util.CommitMutation drives a MODULE exposing commit(environment, input, onSuccess, onError,
// uploadables) — never a raw graphql document. The csrf token and socket client id ride inside
// input, matching every other mutation module.
const SendQuoteMutation = {
	commit(
		environment: Environment,
		input: SendQuoteInput,
		onSuccess: (response: unknown, errors: readonly PayloadError[] | null | undefined) => void,
		onError: (error: Error) => void,
		uploadables?: UploadableMap | null
	) {
		return trackAndCommitMutation<MutationParameters>(environment, {
			mutation,
			variables: {
				input: {
					csrfToken: Util.getCsrfValue(),
					socketClientId: Util.getClientId(),
					...input,
				},
			},
			uploadables: uploadables ?? undefined,
			onCompleted: onSuccess,
			onError,
		} as never);
	},
};

/**
 * Sends the quote. Always via Util.CommitMutation — never react-relay's commitMutation — so the
 * correlation-ID error modal and the logged-out handling still apply.
 *
 * The mutation query re-reads quote.status AND project.quoteActivities, which is what repaints the
 * lock banner and appends the new log entry without a page refetch.
 */
export function sendQuote(
	input: SendQuoteInput,
	onSuccess: () => void,
	onError?: (validationMessageId: string | null) => void
): void {
	Util.CommitMutation(
		SendQuoteMutation,
		input,
		onSuccess,
		// retry=false, deliberately. This mutation emails the client, and the backend treats a
		// re-send of an already-SENT quote as a legitimate resend rather than a no-op
		// (QuoteSendValidator.validate). So when a transient network error loses the RESPONSE to a
		// send the server already committed, Util.CommitMutation's automatic 500ms retry would mail
		// the client a second time and write a second audit row, with the user seeing only success.
		// The modal's `submitting` guard cannot prevent that — the retry fires inside the framework,
		// not from a second click. A genuinely un-sent request surfaces the error modal instead and
		// the user re-clicks Send, which is the safe direction to fail in for a non-idempotent send.
		false,
		null,
		(transaction: unknown, onFailure: (t: unknown) => unknown) => {
			// A recognised refusal renders INSIDE the modal. The global warning path replaces the
			// whole modal stack, which unmounts the composer and destroys the subject, body and
			// recipients — and the non-critical path renders behind this modal where it cannot be
			// read. Inline is the only placement that is both visible and draft-preserving.
			const validationMessageId = getValidationErrorMessageId(String(transaction));
			if (validationMessageId) {
				if (onError) onError(validationMessageId);
				return;
			}
			// Anything else falls through to the framework handler, or the readable error modal
			// (and its correlation id) is swallowed. It answers true only when it has scheduled a
			// retry of this mutation; with retry disabled above it never can, but the guard stays
			// so re-enabling retry cannot silently re-enable the double-send by re-arming Send
			// mid-flight.
			const retryScheduled = onFailure(transaction);
			if (retryScheduled !== true && onError) onError(null);
		}
	);
}
